import {
  dunningFeeMailLabel, dunningQrBill, fmtDate, dunningStageLabel, footerLinesOf, interestBasisFor, invoiceLogoPath, invoicePdfModelFromSource, mailVariables, moneyLocaleFor,
  dunningStageMailLabel, dunningTextKey, emailFooterLines, paragraphsFromMailHtml, renderDunningLetterPdf, renderDunningLetterSvg, renderMailText, type DunningCosts, type DunningLetterModel, type DunningPolicy, type SnapshotParty,
} from "@talentkontor/domain";
import { api } from "../../../data";
import { invoiceLogo, orgPartyOf, storageSafeName } from "../../../model/invoice-document";
import { geltendeVorlage } from "../../../model/beleg-vorlagen";
import { docInputsOf, type InvoiceDetail } from "./detail-data";
import { docSourceOf } from "../../../model/invoice-document";

/**
 * Mahnschreiben als PDF (Befund 13): gleiches Schreiben wie die Mail, dazu die Forderungsaufstellung aus computeDunningCosts
 * (Hauptforderung, Gebühr, Zinsen mit Satz und Zeitraum), Frist und Bankverbindung der Rechnung. Abgelegt im Ordner der Rechnung
 * und als Anhang der Mahnmail übergeben (p_document_path). Die Aufstellung steht zusätzlich im Verlauf (p_costs), damit sich das
 * Schreiben später genauso wieder öffnen lässt.
 */

/** Text der Stufe, Stufenname im Betreff und Fußzeile: in der Fachlogik, damit der Mahn-Autopilot (0570) dasselbe rechnet */
export { dunningTextKey, dunningStageMailLabel, emailFooterLines };

export interface LetterInput {
  d: InvoiceDetail;
  orgName: string;
  policy: DunningPolicy;
  stageKey: string;
  text: { subject: string; body: string } | null;
  lang: "de" | "en";
  date: string;
  deadline: string;
  costs: Pick<DunningCosts, "principalCents" | "feeCents" | "interestCents" | "totalCents" | "interest" | "feeBasis">;
  dueDate: string | null;
  daysOverdue: number;
  paidCents: number;
  creditor?: string | null;
  /** SAAS-3: Empfänger ist eine Privatperson (Rechtsgrundlage des Verbraucherzinses) */
  consumer?: boolean;
}

/**
 * Probelauf N4: Wer schreibt das Mahnschreiben? Bei „Creator mit Vollmacht an Marke“ (talent_to_brand) die Agentur im Auftrag
 * des Creators (Gläubiger), sonst der Aussteller der Rechnung.
 */
export function letterSenderOf(d: InvoiceDetail, orgName: string, creditor?: string | null): {
  vollmacht: boolean; agency: SnapshotParty | null; letterhead: SnapshotParty | null; creditor: string | null;
} {
  const issuer = (d.invoice["issuer_snapshot"] as SnapshotParty | null) ?? null;
  const vollmacht = d.invoice.kind === "talent_to_brand";
  const agency = vollmacht ? orgPartyOf(d.org as Record<string, unknown> | null, d.legal, orgName) : null;
  return {
    vollmacht, agency, letterhead: agency ?? issuer,
    creditor: creditor ?? (vollmacht && d.talent ? [d.talent.first_name, d.talent.last_name].filter(Boolean).join(" ") : null),
  };
}

export async function dunningLetterPdf(x: LetterInput): Promise<{ bytes: Uint8Array; filename: string }> {
  const { model, filename } = await dunningLetterModel(x);
  return { bytes: renderDunningLetterPdf(model), filename };
}

/**
 * K-410: Vorschau des Mahnschreibens im Versandfenster als Seitenbilder (SVG). Dasselbe Modell und derselbe Seitenaufbau wie das
 * PDF, das beim Versand an die Mail geht; nur die Ausgabe ist ein Bild statt einer PDF-Datei.
 */
export async function dunningLetterSheets(x: LetterInput): Promise<{ pages: string[]; filename: string }> {
  const { model, filename } = await dunningLetterModel(x);
  return { pages: renderDunningLetterSvg(model), filename };
}

/** Modell des Mahnschreibens (Absender, Empfänger, Text, Forderungsaufstellung, Bank, Logo) und Dateiname */
export async function dunningLetterModel(x: LetterInput): Promise<{ model: DunningLetterModel; filename: string }> {
  const inv = x.d.invoice;
  const issuer = (inv["issuer_snapshot"] as SnapshotParty | null) ?? null;
  // Probelauf N4: „Creator mit Vollmacht an Marke“ mahnt die Agentur im Auftrag des Creators. Absender (Briefkopf, Logo,
  // Fußzeile) ist die Agentur, darunter „Im Auftrag von <Creator>“; Bankverbindung bleibt die der Rechnung (des Creators)
  const { vollmacht, agency, letterhead, creditor } = letterSenderOf(x.d, x.orgName, x.creditor);
  const logo = await invoiceLogo(invoiceLogoPath(letterhead ?? { logo_path: x.d.org?.logo_path ?? null }));
  // Absender und Empfänger wie auf der Rechnung (eingefroren), Bank der Rechnung
  const source = docSourceOf(docInputsOf(x.d, x.orgName, null));
  const model = invoicePdfModelFromSource({ ...source, language: x.lang });
  const sender = agency ? invoicePdfModelFromSource({ ...source, issuer: agency, language: x.lang }).issuer : model.issuer;
  const toCreator = inv.kind === "agency_to_talent";
  const vars = mailVariables({
    number: inv.number, amount_cents: inv.gross_cents, currency: inv.currency, due_date: x.dueDate, brand: toCreator ? null : x.d.brand?.name,
    deal_title: x.d.deal?.title, days_overdue: x.daysOverdue > 0 ? x.daysOverdue : null, deadline: x.deadline,
    fee_cents: x.costs.feeCents > 0 ? x.costs.feeCents : null, creditor,
    // Teilzahlung (0076): Brief nennt Eingang und offenen Rest statt „kein Zahlungseingang“
    paid_cents: x.paidCents > 0 && x.paidCents < (inv.gross_cents ?? 0) ? x.paidCents : null,
    // Praxistest K-232: wie die Mahnmail (invoice_send_dunning, 0238) Stufenname und Zinsen, sonst stand „eventueller Verzugszinsen“
    stage_label: dunningStageMailLabel(x.policy, x.stageKey, x.lang),
    interest_cents: x.costs.interestCents > 0 ? x.costs.interestCents : null,
    // K-405: Bezeichnung der Gebühr wie in der Aufstellung; K-282/K-408: Land der Agentur für Betreibung bzw. Mahnverfahren
    fee_label: x.costs.feeCents > 0 ? dunningFeeMailLabel(x.costs.feeBasis, x.lang) : null,
    // 0305 (K-420): Grundlage der Gebühr für den Satzteil mit Artikel („die Verzugspauschale von …“, „a €40.00 flat …“)
    fee_basis: x.costs.feeBasis ?? null,
    agency_country: (x.d.org?.country ?? null)?.toUpperCase() ?? null,
  }, { agencyName: (agency ?? issuer)?.name ?? x.orgName, talentFirstName: x.d.talent?.first_name ?? null, locale: model.locale, language: x.lang, link: null });
  // K-420: Daten im Brieftext im selben Format wie Kopf, Aufstellung und Zahlungszeile (englisch „10 Aug 2026“)
  const letterLocale = moneyLocaleFor(x.lang, issuer?.country ?? x.d.org?.country ?? null);
  const briefDatum = (v: string | null | undefined) => fmtDate(v ?? null, x.lang, letterLocale);
  if (vars["invoice.due_date"] && briefDatum(x.dueDate)) vars["invoice.due_date"] = briefDatum(x.dueDate)!;
  if (vars["dunning.deadline"] && briefDatum(x.deadline)) vars["dunning.deadline"] = briefDatum(x.deadline)!;
  const rendered = x.text ? renderMailText(x.text, vars) : null;
  // Gruß mit Agenturnamen bleibt, Link-Absätze entfallen (Brief ohne Portal-Link)
  const paragraphs = rendered ? paragraphsFromMailHtml(rendered.html.replace(/<p>\s*<a [^>]*>[^<]*<\/a>\s*<\/p>/g, "")) : [];
  const title = dunningStageLabel(x.policy, x.stageKey, x.lang);
  const letter: DunningLetterModel = {
    language: x.lang, locale: letterLocale, currency: model.currency,
    sender, onBehalfOf: vollmacht ? (issuer?.name ?? creditor) : null, footerLines: footerLinesOf(letterhead ?? {}, x.lang), recipient: model.recipient, date: x.date, title,
    // K-404: Zahlungserinnerung gelb, jede Mahnung rot (nach Stufentyp)
    tone: x.policy.stages.find((s) => s.key === x.stageKey)?.isReminder ? "reminder" : "notice",
    invoice: { number: inv.number ?? "", date: inv.issue_date, dueDate: x.dueDate, grossCents: inv.gross_cents ?? 0 },
    paragraphs, costs: x.costs, paidCents: x.paidCents, deadline: x.deadline, bank: model.bank ?? null,
    // 0181: Rechtsgrundlage nach dem Land, dessen Sätze die Mahnleiter rechnet (Sitzland des Kontos), nicht nach dem Aussteller
    interestBasis: interestBasisFor(x.d.org?.country ?? issuer?.country ?? null, !!x.consumer), accent: model.accent ?? null, logo: logo?.image ?? null,
    // Fußzeile für Mails und Mahnschreiben (Branding, SAAS-R3-9)
    closingLines: emailFooterLines((x.d.org as Record<string, unknown> | null)?.["email_footer"]),
    // Schweiz: neuer Zahlteil über den offenen Gesamtbetrag mit der Referenz der Rechnung (PRAXIS-R3-6)
    qrBill: dunningQrBill(model.qrBill ?? null, x.costs.totalCents, title, inv.number ?? ""),
    // PDF-Vorlage für Mahnschreiben (0456): Marke vor Standard der Agentur
    layout: await geltendeVorlage(inv.org_id, "dunning", (inv["brand_id"] as string | null) ?? null),
  };
  const word = x.lang === "en" ? "Dunning" : "Mahnschreiben";
  return { model: letter, filename: `${word}_${title.replace(/\s+/g, "_")}_${inv.number ?? ""}_${x.date}.pdf` };
}

/** Schreiben im Ordner der Rechnung ablegen; Pfad für invoice_send_dunning */
export async function uploadDunningLetter(orgId: string, invoiceId: string, file: { bytes: Uint8Array; filename: string }): Promise<string> {
  const path = `${orgId}/invoice/${invoiceId}/${Date.now()}_${storageSafeName(file.filename)}`;
  await api.upload("documents", path, new Blob([file.bytes as BlobPart], { type: "application/pdf" }));
  return path;
}
